import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Target,
  FileText,
  FileSearch,
  Shield,
  Plane,
  ArrowRight,
  Search,
  Building2,
  CheckCircle2,
  ClipboardList,
} from 'lucide-react';
import ScrollReveal from '../components/ScrollReveal';
import StudentEvaluationForm from '../components/StudentEvaluationForm';

const heroImages = [
  'https://images.unsplash.com/photo-1768339745966-8dc43f5b36d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxPeGZvcmQlMjBVbml2ZXJzaXR5JTIwaGlzdG9yaWMlMjBidWlsZGluZyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3Njk3NTM1NDZ8MA&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1767410913219-2db49c0575be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxIYXJ2YXJkJTIwVW5pdmVyc2l0eSUyMGNhbXB1cyUyMGJ1aWxkaW5nfGVufDB8fHx8MTc2OTc1MzU0OHww&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1680907305042-284cb3a97be2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxDYW1icmlkZ2UlMjBVbml2ZXJzaXR5JTIwaGlzdG9yaWMlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzY5NzUzNTUwfDA&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1762410281840-686c2d40cd42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxTdGFuZm9yZCUyMFVuaXZlcnNpdHklMjBjYW1wdXMlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzY5NzUzNTUzfDA&ixlib=rb-4.1.0&q=85',
  'https://images.pexels.com/photos/16361673/pexels-photo-16361673.jpeg',
  'https://images.pexels.com/photos/14495769/pexels-photo-14495769.jpeg',
  'https://images.pexels.com/photos/34899083/pexels-photo-34899083.jpeg',
  'https://images.pexels.com/photos/10353017/pexels-photo-10353017.jpeg',
];

const services = [
  { icon: Target, title: 'University Selection Strategy', description: 'We assess your academic profile, budget, and career goals to identify universities and countries that genuinely fit your situation \u2014 not a generic list.' },
  { icon: FileText, title: 'Application Preparation', description: 'From personal statements to application forms, we review, refine, and manage every step of the submission process to each institution.' },
  { icon: FileSearch, title: 'Document Review & SOP Guidance', description: 'We review transcripts, draft statements of purpose, and ensure every document meets the specific requirements of your target universities.' },
  { icon: Shield, title: 'Visa Documentation Support', description: 'We prepare financial documents, sponsor letters, and visa application forms with country-specific guidance for your destination.' },
  { icon: Plane, title: 'Pre-Departure Guidance', description: 'Accommodation, banking, insurance, orientation \u2014 we prepare you for arrival so you can focus on what matters.' },
];

const processSteps = [
  { step: '01', title: 'Profile Evaluation', description: 'We review your academic background, finances, and goals.' },
  { step: '02', title: 'University Shortlisting', description: 'We identify universities and countries that match your profile.' },
  { step: '03', title: 'Application & Documentation', description: 'We prepare and submit your applications with all required documents.' },
  { step: '04', title: 'Visa Preparation', description: 'We guide you through the full visa documentation and interview process.' },
];

const officeImages = [
  { src: 'https://customer-assets.emergentagent.com/job_ba4b6351-8570-4d37-8ada-e860f15e5a88/artifacts/1zv14qig_IMG_3674.jpeg', alt: 'DAAS Global - Director Office' },
  { src: 'https://customer-assets.emergentagent.com/job_ba4b6351-8570-4d37-8ada-e860f15e5a88/artifacts/xutvnfe1_IMG_3677.jpeg', alt: 'DAAS Global - Consultation Room' },
  { src: 'https://customer-assets.emergentagent.com/job_ba4b6351-8570-4d37-8ada-e860f15e5a88/artifacts/y2dcamwp_IMG_3675.jpeg', alt: 'DAAS Global - Meeting Room' },
  { src: 'https://customer-assets.emergentagent.com/job_ba4b6351-8570-4d37-8ada-e860f15e5a88/artifacts/3grvhjf3_IMG_3681.jpeg', alt: 'DAAS Global - Work Area' },
];

const universityLogos = [
  { name: 'University of Melbourne', logo: 'https://customer-assets.emergentagent.com/job_ba4b6351-8570-4d37-8ada-e860f15e5a88/artifacts/berm8e3t_8071229d-a439-42d6-97a9-f364f5f1aff1.jpeg' },
  { name: 'University of Sydney', logo: 'https://customer-assets.emergentagent.com/job_ba4b6351-8570-4d37-8ada-e860f15e5a88/artifacts/j703nuru_4c2b9abb-d433-4465-af34-ba0a8cf31aa1.jpeg' },
  { name: 'Duke University', logo: 'https://customer-assets.emergentagent.com/job_ba4b6351-8570-4d37-8ada-e860f15e5a88/artifacts/qmi9tzbu_02431d0f-4fb6-4505-b825-7ef577dc0ac3.jpeg' },
  { name: 'University of Toronto', logo: 'https://customer-assets.emergentagent.com/job_ba4b6351-8570-4d37-8ada-e860f15e5a88/artifacts/vhqxb9ux_11444c10-64d6-4c97-b51d-335db507647a.jpeg' },
  { name: 'University of British Columbia', logo: 'https://customer-assets.emergentagent.com/job_ba4b6351-8570-4d37-8ada-e860f15e5a88/artifacts/9zibl0rx_54c19c41-174c-4af9-8488-9b9d1a9097ca.jpeg' },
  { name: 'University of Sheffield', logo: 'https://customer-assets.emergentagent.com/job_ba4b6351-8570-4d37-8ada-e860f15e5a88/artifacts/9o0etcft_IMG_2233.webp' },
  { name: 'Durham University', logo: 'https://customer-assets.emergentagent.com/job_ba4b6351-8570-4d37-8ada-e860f15e5a88/artifacts/zyjosj3u_ab5bf451-4873-4368-9e33-cf5370009872.jpeg' },
  { name: 'University of Auckland', logo: 'https://customer-assets.emergentagent.com/job_ba4b6351-8570-4d37-8ada-e860f15e5a88/artifacts/pd3s8uf7_edb88247-8e39-48eb-b88f-faee21a9993e.jpeg' },
];

const Home = () => {
  const [currentBg, setCurrentBg] = useState(0);
  const [showEvalForm, setShowEvalForm] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBg((prev) => (prev + 1) % heroImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  return (
    <main data-testid="home-page">
      {/* ── Hero ── */}
      <section className="relative h-[82vh] min-h-[520px] overflow-hidden" data-testid="hero-section">
        {heroImages.map((img, i) => (
          <div key={i} className={`absolute inset-0 transition-opacity duration-[1500ms] ${i === currentBg ? 'opacity-100' : 'opacity-0'}`}>
            <img src={img} alt="" className="w-full h-full object-cover" loading={i < 2 ? 'eager' : 'lazy'} />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/95 via-[#0F172A]/85 to-[#0F172A]/50 z-10" />
        <div className="relative z-20 h-full flex items-center">
          <div className="container-main">
            <div className="max-w-[620px]">
              <p className="text-[var(--secondary)] text-[11px] font-bold uppercase tracking-[0.25em] mb-4 opacity-0 animate-fade-in-up">
                DAAS Global &mdash; A Subsidiary of DAAS Limited
              </p>
              <h1
                className="text-[2.25rem] sm:text-5xl lg:text-[3.5rem] font-black text-white tracking-tight leading-[1.12] mb-4 opacity-0 animate-fade-in-up"
                style={{ animationDelay: '0.1s' }}
                data-testid="hero-headline"
              >
                Apply to Universities Worldwide with Structured Guidance
              </h1>
              <p className="text-white/65 text-[15px] md:text-base mb-7 leading-relaxed max-w-[540px] opacity-0 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                We guide students through university selection, applications, and visa preparation with a clear and disciplined process.
              </p>
              <div className="opacity-0 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                <button
                  onClick={() => setShowEvalForm(true)}
                  className="inline-flex items-center justify-center px-7 py-3.5 bg-[var(--secondary)] hover:bg-[#b8944e] text-white text-[11px] font-bold uppercase tracking-[0.18em] transition-all duration-300 hover:-translate-y-0.5"
                  data-testid="hero-cta-btn"
                >
                  Get Your Free Profile Evaluation
                  <ArrowRight size={15} className="ml-2" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Human Line ── */}
      <section className="py-5 bg-[var(--primary)] border-t border-white/10" data-testid="human-line">
        <div className="container-main">
          <p className="text-white/70 text-sm text-center leading-relaxed italic">
            We understand that studying abroad is a life-changing decision. Our role is to guide you through it with clarity and structure.
          </p>
        </div>
      </section>

      {/* ── How We Work ── */}
      <ScrollReveal>
        <section className="section-padding bg-white" data-testid="how-we-work-section">
          <div className="container-main">
            <div className="text-center mb-8">
              <p className="text-[var(--secondary)] text-[11px] font-bold uppercase tracking-[0.2em] mb-2">Our Process</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[var(--primary)] tracking-tight mb-3">How We Work</h2>
              <div className="gold-accent-line mx-auto" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {processSteps.map((s, i) => (
                <div key={s.step} className="p-5 bg-[var(--background)] border border-[var(--border)] transition-all duration-300 hover:border-[var(--secondary)]/30 hover:shadow-sm" data-testid={`process-step-${i}`}>
                  <span className="text-[var(--secondary)] text-xs font-bold tracking-widest mb-2 block">STEP {s.step}</span>
                  <h3 className="text-base font-bold text-[var(--primary)] mb-1.5">{s.title}</h3>
                  <p className="text-[var(--text-secondary)] text-sm leading-relaxed">{s.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* ── Services ── */}
      <section className="section-padding bg-[var(--background)]" data-testid="services-section">
        <div className="container-main">
          <ScrollReveal>
            <div className="max-w-xl mb-8">
              <p className="text-[var(--secondary)] text-[11px] font-bold uppercase tracking-[0.2em] mb-2">What We Do</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[var(--primary)] tracking-tight mb-3">Our Services</h2>
              <div className="gold-accent-line" />
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((s, i) => (
              <ScrollReveal key={s.title} delay={i * 70}>
                <div className="service-card h-full" data-testid={`service-card-${i}`}>
                  <div className="service-card-icon">
                    <s.icon size={22} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-bold text-[var(--primary)] mb-1.5">{s.title}</h3>
                  <p className="text-[var(--text-secondary)] leading-relaxed text-sm">{s.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal delay={400}>
            <div className="text-center mt-8">
              <button
                onClick={() => setShowEvalForm(true)}
                className="inline-flex items-center justify-center px-7 py-3.5 bg-[var(--primary)] hover:bg-[#1e293b] text-white text-[11px] font-bold uppercase tracking-[0.18em] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
                data-testid="services-cta-btn"
              >
                <Search size={15} className="mr-2" />
                Get Your Free Profile Evaluation
              </button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── Credibility ── */}
      <ScrollReveal>
        <section className="py-10 md:py-12 bg-white border-y border-[var(--border)]" data-testid="credibility-section">
          <div className="container-main">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[var(--background)] flex-shrink-0 mt-0.5">
                  <Building2 size={20} className="text-[var(--secondary)]" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-[var(--primary)] mb-1">Corporate Oversight</h4>
                  <p className="text-[var(--text-secondary)] text-sm leading-relaxed">DAAS Global operates under DAAS Limited, ensuring structured oversight and long-term operational stability.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[var(--background)] flex-shrink-0 mt-0.5">
                  <ClipboardList size={20} className="text-[var(--secondary)]" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-[var(--primary)] mb-1">Process-Driven</h4>
                  <p className="text-[var(--text-secondary)] text-sm leading-relaxed">Every student case follows documented procedures with clear accountability at each stage.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[var(--background)] flex-shrink-0 mt-0.5">
                  <CheckCircle2 size={20} className="text-[var(--secondary)]" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-[var(--primary)] mb-1">Ethical Standards</h4>
                  <p className="text-[var(--text-secondary)] text-sm leading-relaxed">We provide realistic guidance. No false promises, no guaranteed outcomes.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* ── Office Showcase ── */}
      <ScrollReveal>
        <section className="section-padding bg-[var(--background)]" data-testid="office-section">
          <div className="container-main">
            <div className="text-center mb-8">
              <p className="text-[var(--secondary)] text-[11px] font-bold uppercase tracking-[0.2em] mb-2">Our Office</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[var(--primary)] tracking-tight mb-3">Visit Us</h2>
              <div className="gold-accent-line mx-auto mb-4" />
              <p className="text-[var(--text-secondary)] text-sm max-w-xl mx-auto leading-relaxed">
                Welcome to our office. This is where we work closely with students and families to guide them through every step of their academic journey.
              </p>
            </div>
            {/* Photo Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="office-photo-grid">
              {officeImages.map((img, i) => (
                <div key={i} className="aspect-[4/3] overflow-hidden group" data-testid={`office-photo-${i}`}>
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
            {/* Address */}
            <div className="text-center">
              <div className="inline-block text-sm text-[var(--text-secondary)] bg-white border border-[var(--border)] px-6 py-3">
                <span className="font-semibold text-[var(--primary)]">Rupayan Shopping Square (4th Floor)</span>
                <span className="mx-2 text-[var(--border)]">|</span>
                Bashundhara R/A, Dhaka-1229, Bangladesh
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* ── University Partners ── */}
      <section className="section-padding bg-white" data-testid="partners-section">
        <div className="container-main">
          <ScrollReveal>
            <div className="text-center mb-8">
              <p className="text-[var(--secondary)] text-[11px] font-bold uppercase tracking-[0.2em] mb-2">Our Network</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[var(--primary)] tracking-tight mb-3">Some of Our University Partners</h2>
              <div className="gold-accent-line mx-auto" />
            </div>
          </ScrollReveal>
          <ScrollReveal delay={80}>
            <div className="flex flex-wrap justify-center gap-4">
              {universityLogos.map((uni) => (
                <div
                  key={uni.name}
                  className="group w-36 h-24 bg-[var(--background)] border border-[var(--border)] flex items-center justify-center p-3 transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 hover:border-[var(--secondary)]/30 cursor-pointer"
                  title={uni.name}
                  data-testid={`partner-logo-${uni.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <img src={uni.logo} alt={uni.name} className="max-h-[60px] w-auto max-w-full object-contain transition-transform duration-300 group-hover:scale-105" loading="lazy" />
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <ScrollReveal>
        <section className="py-10 md:py-12 bg-[var(--primary)]" data-testid="cta-section">
          <div className="container-main text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-3">Ready to Start?</h2>
            <p className="text-white/60 text-sm mb-6 max-w-md mx-auto">Get a free evaluation of your academic profile. No obligations.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => setShowEvalForm(true)}
                className="inline-flex items-center justify-center px-7 py-3.5 bg-[var(--secondary)] hover:bg-[#b8944e] text-white text-[11px] font-bold uppercase tracking-[0.18em] transition-all duration-300 hover:-translate-y-0.5"
                data-testid="bottom-cta-btn"
              >
                Start Your Application Process
                <ArrowRight size={15} className="ml-2" />
              </button>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-7 py-3.5 border border-white/30 text-white text-[11px] font-bold uppercase tracking-[0.18em] transition-all duration-300 hover:bg-white/10 hover:-translate-y-0.5"
                data-testid="cta-contact-btn"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </section>
      </ScrollReveal>

      <StudentEvaluationForm isOpen={showEvalForm} onClose={() => setShowEvalForm(false)} />
    </main>
  );
};

export default Home;
